import { Form, Select } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { UserLookupProps } from './type';
import { useSelector } from 'react-redux';
import { ProfessionalType } from '../../../../types';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useAdminSignInAsProfessional } from '../../../../hooks/admin/useAdminProfessional';
import { GET_PROFESSIONAL_LIST_CLIENTS_LIGHT } from '../../../../services/graphql/queries/professional';

const UserLookup: React.FC<UserLookupProps> = ({
  multiSelect,
  professionalOnly,
  clientOnly,
  form,
}) => {
  const professionals = useSelector((state: any) => state.professionals.professionals);
  const clients = useSelector((state: any) => state.professionals.listClients);
  const [selectedProfessional, setSelectedProfessional] = useState<string>();
  const [selectedClient, setSelectedClient] = useState<string>();
  const [impersonatedClients, setImpersonatedClients] = useState<any[]>([]);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const { signInAsProfessional } = useAdminSignInAsProfessional();
  const apolloClient = useApolloClient();

  // For admin users, we need to get all clients across all professionals
  // Since the professionalListClients query requires Professional auth,
  // we'll use the listAnalysesView query which admins CAN access to get client data
  const { data: analysesData, loading: loadingClients, error: clientsError } = useQuery(
    gql`
      query userLookupListAnalysesView($viewType: String!, $page: Int, $perPage: Int) {
        listAnalysesView(viewType: $viewType, page: $page, perPage: $perPage) {
          data {
            client
            clientGid
            professional
            professionalCode
          }
        }
      }
    `,
    {
      variables: {
        viewType: 'hma', // Get HMA data which includes client info
        page: 1,
        perPage: 1000,
      },
      skip: clients && clients.length > 0, // Skip if clients already in Redux (professional user)
    }
  );

  // Debug: Log when query data changes
  useEffect(() => {
    console.log('=== UserLookup Query State ===');
    console.log('Loading:', loadingClients);
    console.log('Error:', clientsError);
    console.log('AnalysesData:', analysesData);
    console.log('Skip query?', clients && clients.length > 0);
  }, [analysesData, loadingClients, clientsError, clients]);

  const optionProfessionalData = useMemo(() => {
    return professionals?.map((professional: ProfessionalType) => ({
      value: professional.id,
      label: `${professional.firstName} ${professional.familyName}`
    })) || []
  }, [professionals]);

  // Use clients from Redux state if available (for professionals), 
  // otherwise extract unique clients from analyses data (for admins)
  const optionClientData = useMemo(() => {
    let clientsList: any[] = [];

    if (clients && clients.length > 0) {
      // Professional user - use Redux clients
      clientsList = clients;
    } else if (impersonatedClients && impersonatedClients.length > 0) {
      // Admin user who successfully ghost-logged into a professional
      clientsList = impersonatedClients;
    } else if (analysesData?.listAnalysesView?.data) {
      // Fallback for admins: extract unique clients from analyses data
      const analysesClients = analysesData.listAnalysesView.data;
      const uniqueClientsMap = new Map();

      analysesClients.forEach((item: any) => {
        const clientKey = `${item.client}_${item.clientGid}`;
        if (!uniqueClientsMap.has(clientKey) && item.client && item.clientGid) {
          // Extract numeric ID from GID if it's in the format "gid://app/Client/123"
          let numericId = item.clientGid;
          if (typeof item.clientGid === 'string' && item.clientGid.includes('/')) {
            const parts = item.clientGid.split('/');
            numericId = parts[parts.length - 1];
          }

          uniqueClientsMap.set(clientKey, {
            id: numericId,
            firstName: item.client.split(' ')[0] || item.client,
            familyName: item.client.split(' ').slice(1).join(' ') || '',
            fullName: item.client,
            professionalCode: item.professionalCode
          });
        }
      });

      const allClients = Array.from(uniqueClientsMap.values());

      // Filter by selected professional's code if a professional is selected
      if (selectedProfessional) {
        const selectedProfObj = professionals?.find((p: any) => p.id === selectedProfessional);
        const profCode = selectedProfObj?.code;

        if (profCode) {
          clientsList = allClients.filter((c: any) => c.professionalCode === profCode);
        } else {
          clientsList = allClients;
        }
      } else {
        clientsList = allClients;
      }
    }

    console.log('UserLookup - final clientsList:', clientsList);

    return clientsList.map((client: any) => ({
      value: client.id,
      label: client.fullName || `${client.firstName} ${client.familyName}`.trim()
    }));
  }, [clients, impersonatedClients, analysesData, selectedProfessional, professionals]);

  const handleSelectProfessional = async (newValue: string) => {
    setSelectedProfessional(newValue);
    // Reset client selection when professional changes
    setSelectedClient(undefined);
    setImpersonatedClients([]);
    // Reset the form field value for client
    if (form) {
      form.setFieldValue('client', undefined);
    }

    // GHOST LOGIN LOGIC for Admins
    const prof = professionals?.find((p: any) => p.id === newValue);
    // If we are an Admin (no clients in redux) and have a professional selected
    if (prof && (!clients || clients.length === 0)) {
      try {
        setIsImpersonating(true);
        console.log('UserLookup - Starting Ghost Login for:', prof.email);

        const result = await signInAsProfessional(prof.email);
        const tempToken = result?.token;

        if (tempToken) {
          console.log('UserLookup - Ghost Login success, fetching client list...');
          const { data } = await apolloClient.query({
            query: GET_PROFESSIONAL_LIST_CLIENTS_LIGHT,
            variables: { page: 1, perPage: 1000 },
            context: {
              headers: {
                Authorization: `Bearer ${tempToken}`
              }
            },
            fetchPolicy: 'network-only'
          });

          if (data?.professionalListClients?.data) {
            setImpersonatedClients(data.professionalListClients.data);
            console.log('UserLookup - Ghost fetched clients:', data.professionalListClients.data.length);
          }
        }
      } catch (err) {
        console.error('UserLookup - Ghost Login failed:', err);
      } finally {
        setIsImpersonating(false);
      }
    }
  };

  const handleSelectClient = (newValue: string) => {
    setSelectedClient(newValue);
  };

  return (
    <>
      {!clientOnly && (
        <Form.Item
          name="professional"
          label="Professional"
          rules={[
            {
              required: true,
              message: 'Professional can\'t be blank',
            },
          ]}
        >
          {/* <StyledButtonInput
          onClick={() => setOpenUserModal(true)}
        >
          <span>{placeholder}</span>
        </StyledButtonInput> */}
          <Select
            mode={multiSelect ? 'multiple' : undefined}
            showSearch
            value={selectedProfessional}
            placeholder={'Find Professional ...'}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => option.label?.includes(input)}
            onChange={handleSelectProfessional}
            notFoundContent={null}
            options={(optionProfessionalData || []).map((d: any) => ({
              value: d.value,
              label: d.label,
            }))}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}
      {!professionalOnly && (
        <Form.Item
          name="client"
          label="Client"
          rules={[
            {
              required: true,
              message: 'Client can\'t be blank',
            },
          ]}
        >
          <Select
            mode={multiSelect ? 'multiple' : undefined}
            showSearch
            value={selectedClient}
            placeholder={loadingClients || isImpersonating ? 'Loading clients...' : 'Find Client ...'}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => option.label?.toLowerCase().includes(input.toLowerCase())}
            onChange={handleSelectClient}
            notFoundContent={loadingClients || isImpersonating ? 'Loading...' : (optionClientData.length === 0 ? 'No clients available' : null)}
            loading={loadingClients || isImpersonating}
            disabled={loadingClients || isImpersonating}
            options={optionClientData}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}
      {/* <Modal
        title={`${userType} lookup`}
        open={openUserModal}
        onOk={() => setOpenUserModal(false)}
        onCancel={() => setOpenUserModal(false)}
        footer={null}
        width={900}
      >
        <ModalContentWrapper>
          <Select
            showSearch
            value={value}
            placeholder={'Find Professional ...'}
            defaultActiveFirstOption={false}
            suffixIcon={null}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => option.label?.includes(input)}
            onSearch={handleSearch}
            onChange={handleChange}
            notFoundContent={null}
            options={(optionData || []).map((d: any) => ({
              value: d.value,
              label: d.label,
            }))}
            style={{ width: '100%' }}
          />
        </ModalContentWrapper>
      </Modal> */}
    </>
  )
}

export default UserLookup